/** @format */

import React, { FC, memo, MouseEventHandler } from "react";
import { useGlobalState } from "senf-shared";
import { useTranslation } from "react-i18next";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import { bodySmBold } from "../../core/typography/Typography";
import { checkIfHex, checkIfHSLA } from "../../../styles/helpers";

export interface TopicTagProps {
  text?: JSX.Element | string;
  icon?: string;
  iconColor?: string;
  width?: "max" | undefined;
  active?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  hide?: boolean;
  name?: string; // added the missing name prop
  maxWidth?: boolean; // added the missing maxWidth prop
}

const TopicTag: FC<TopicTagProps> = ({
  text,
  icon,
  iconColor,
  active,
  name,
  maxWidth,
  ...rest
}) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const isTailwindColor = !checkIfHSLA(iconColor) && !checkIfHex(iconColor);

  const colors = [
    "text-tag-surface-selected-red",
    "text-tag-surface-selected-rose",
    "text-tag-surface-selected-purple",
    "text-tag-surface-selected-blue",
    "text-tag-surface-selected-teal",
    "text-tag-surface-selected-green",
    "text-tag-surface-selected-yellow",
    "text-tag-surface-selected-orange",
    "text-tag-surface-selected-brown",
    "text-tag-surface-selected-grey",
    "text-tag-surface-selected-lightgrey",
  ];

  if (name === "spacing") {
    return <div className="h-6" />; // Skip rendering if the name is "spacing"
  }

  return (
    <Button
      className={cn(
        `button whitespace-nowrap`,
        bodySmBold,
        active ? "" : "bg-white",
        maxWidth && `line-clamp-1 max-w-[280px] justify-start`
      )}
      type="button"
      leadingIcon={icon}
      leadingIconProps={{
        className: cn(
          active && !iconColor && "text-text-on-primary",
          icon?.startsWith("ideaCategory") &&
            "mr-1 h-3 w-3 rounded-full bg-white ring-1 ring-white",
          isTailwindColor && `text-tag-surface-selected-${iconColor}`
        ),
      }}
      style={!isTailwindColor ? { color: iconColor } : {}}
      variant={active ? "primary" : "outline"}
      {...rest}
      aria-label={`${t("global:filter")}: ${translation?.[text] || text}, ${
        active ? t("global:active") : t("global:deactivated")
      }`}
    >
      <span className={active ? "text-text-on-primary" : "text-text-default"}>
        {translation?.[text] || text}
      </span>
    </Button>
  );
};

export default memo(TopicTag);
